export const DECK_CHART_PRIMARY_COLOR = 'var(--slide-chart-1)' as const

export const DECK_CHART_SERIES_COLORS: Array<string> = [
  'var(--slide-chart-1)',
  'var(--slide-chart-2)',
  'var(--slide-chart-3)',
  'var(--slide-chart-4)',
  'var(--slide-chart-5)',
  'var(--slide-chart-6)',
  'var(--slide-chart-7)',
  'var(--slide-chart-8)',
]

export const DECK_CHART_INSIDE_LABEL_DARK = 'var(--slide-text-primary, #000000)' as const
export const DECK_CHART_INSIDE_LABEL_LIGHT = 'var(--slide-cover-text, #ffffff)' as const

interface RgbColor {
  r: number
  g: number
  b: number
}

export interface ChartLabelColorOptions {
  lightText?: string
  darkText?: string
  fallback?: string
}

export function getDeckChartSeriesColor(index: number): string {
  return DECK_CHART_SERIES_COLORS[index % DECK_CHART_SERIES_COLORS.length]
}

function hslToRgb(h: number, s: number, l: number): RgbColor {
  const hue = ((h % 360) + 360) % 360
  const sat = Math.max(0, Math.min(100, s)) / 100
  const light = Math.max(0, Math.min(100, l)) / 100
  const c = (1 - Math.abs(2 * light - 1)) * sat
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1))
  const m = light - c / 2

  let r1 = 0
  let g1 = 0
  let b1 = 0

  if (hue < 60) {
    r1 = c
    g1 = x
  } else if (hue < 120) {
    r1 = x
    g1 = c
  } else if (hue < 180) {
    g1 = c
    b1 = x
  } else if (hue < 240) {
    g1 = x
    b1 = c
  } else if (hue < 300) {
    r1 = x
    b1 = c
  } else {
    r1 = c
    b1 = x
  }

  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  }
}

export function resolveCssColorValue(value: string, fallback = ''): string {
  if (typeof window === 'undefined') {
    return value || fallback
  }

  const resolve = (input: string, depth: number): string => {
    if (depth > 6) {
      return fallback || input
    }
    const trimmed = input.trim()
    if (!trimmed) {
      return fallback
    }

    const varMatch = trimmed.match(/var\(\s*(--[^),\s]+)\s*(?:,\s*([^)]+))?\)/)
    if (!varMatch) {
      return trimmed
    }

    const varName = varMatch[1]
    const inlineFallback = varMatch[2]?.trim() || fallback
    const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim()
    if (!resolved) {
      return inlineFallback ? resolve(inlineFallback, depth + 1) : fallback || trimmed
    }

    return resolve(trimmed.replace(varMatch[0], resolved), depth + 1)
  }

  return resolve(value || fallback, 0)
}

function parseColorToRgb(color: string): RgbColor | null {
  const resolved = resolveCssColorValue(color).trim()
  if (!resolved) {
    return null
  }

  if (resolved.startsWith('#')) {
    const hex = resolved.slice(1)
    const full = hex.length === 3 ? hex.split('').map((x) => x + x).join('') : hex
    if (full.length !== 6) {
      return null
    }
    const n = Number.parseInt(full, 16)
    if (!Number.isFinite(n)) {
      return null
    }
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
  }

  const rgbMatch = resolved.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i)
  if (rgbMatch) {
    return { r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) }
  }

  const hslMatch = resolved.match(/hsla?\s*\(\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)%\s*[,\s]\s*(\d+(?:\.\d+)?)%/i)
  if (hslMatch) {
    return hslToRgb(Number(hslMatch[1]), Number(hslMatch[2]), Number(hslMatch[3]))
  }

  const bareHslMatch = resolved.match(/^(-?\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/)
  if (bareHslMatch) {
    return hslToRgb(Number(bareHslMatch[1]), Number(bareHslMatch[2]), Number(bareHslMatch[3]))
  }

  return null
}

const LABEL_WHITE_RGB: RgbColor = { r: 255, g: 255, b: 255 }
const LABEL_BLACK_RGB: RgbColor = { r: 0, g: 0, b: 0 }
/** WCAG 1.4.3 normal text. In-bar captions are 11px, so this is the floor. */
const INSIDE_LABEL_CONTRAST_FLOOR = 4.5
/** When both sides sit in the 3–5 band, prefer white — 11px type on a
 *  saturated fill reads better than a fractional WCAG win for black. */
const INSIDE_LABEL_CLOSE_CALL = 0.15
const INSIDE_LABEL_WHITE_MIN = 3

function relativeLuminance(color: RgbColor): number {
  const linear = [color.r, color.g, color.b].map((channel) => {
    const normalized = channel / 255
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2]
}

function contrastRatio(a: RgbColor, b: RgbColor): number {
  const first = relativeLuminance(a)
  const second = relativeLuminance(b)
  const lighter = Math.max(first, second)
  const darker = Math.min(first, second)
  return (lighter + 0.05) / (darker + 0.05)
}

/**
 * Red–orange CTA/action fills sit in a mid-luminance band where WCAG often
 * prefers black, but the hue still reads as a dark warm color. Used only for
 * button/CTA foreground — chart labels measure contrast instead.
 *
 * Hue 345–45 covers red through Bloomberg/autumn orange. Saturation and
 * lightness caps keep cream/peach surfaces on dark text.
 */
export function isWarmEmphasisFill(h: number, s: number, l: number): boolean {
  const hue = ((h % 360) + 360) % 360
  const isWarmHue = hue >= 345 || hue < 45
  return isWarmHue && s >= 30 && l <= 72
}

export interface InsideLabelContrast {
  onWhite: number
  onBlack: number
  useWhite: boolean
}

/**
 * Measure white vs black on the fill. Grey, red, and orange all go through
 * this — no hue special case.
 *
 * 1. If only one side clears 4.5:1, use that side.
 * 2. If the ratios are within 15% and white is at least 3:1, use white
 *    (red 4.42 vs black 4.75 — small type on a saturated fill).
 * 3. Otherwise use the higher ratio.
 */
export function measureInsideLabelContrast(backgroundColor: string): InsideLabelContrast | null {
  const rgb = parseColorToRgb(backgroundColor)
  if (!rgb) {
    return null
  }
  const onWhite = contrastRatio(rgb, LABEL_WHITE_RGB)
  const onBlack = contrastRatio(rgb, LABEL_BLACK_RGB)
  const whitePasses = onWhite >= INSIDE_LABEL_CONTRAST_FLOOR
  const blackPasses = onBlack >= INSIDE_LABEL_CONTRAST_FLOOR
  const closer = Math.abs(onWhite - onBlack) / Math.max(onWhite, onBlack, 0.01)
  let useWhite = onWhite >= onBlack
  if (closer <= INSIDE_LABEL_CLOSE_CALL && onWhite >= INSIDE_LABEL_WHITE_MIN) {
    useWhite = true
  } else if (whitePasses !== blackPasses) {
    useWhite = whitePasses
  }
  return { onWhite, onBlack, useWhite }
}

export function fillNeedsWhiteInsideLabel(backgroundColor: string): boolean {
  return measureInsideLabelContrast(backgroundColor)?.useWhite === true
}

export function getChartInsideLabelColor(backgroundColor: string, options: ChartLabelColorOptions = {}): string {
  const lightText = options.lightText ?? DECK_CHART_INSIDE_LABEL_LIGHT
  const darkText = options.darkText ?? DECK_CHART_INSIDE_LABEL_DARK
  const fallback = options.fallback ?? lightText
  const measured = measureInsideLabelContrast(backgroundColor)
  if (!measured) {
    return fallback
  }
  return measured.useWhite ? lightText : darkText
}
